import React, { useEffect, useRef, useState } from 'react';
import tw, { styled } from 'twin.macro';
import { ArrowUUpLeft, ArrowUUpRight } from '@phosphor-icons/react';
import { caretIsInPageUndoField } from 'hooks/useUndoRedo';
import {
  AI_BUSY_CHANGED,
  anAnswerIsArriving,
  stopTheArrivingAnswer,
} from 'utils/aiAnswerInProgress';

interface UndoRedoProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

/** Undo and Redo sit where the Save button used to be (D20). */
const UndoRedo: React.FC<UndoRedoProps> = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}) => {
  /*
   * While an AI answer arrives, a press on an arrow only stops the answer; it
   * does not also take a step. The press is recognised at mouse-down, because
   * by the time the click arrives the box has closed and nothing is arriving
   * any more.
   */
  const stoppedAnAnswer = useRef(false);

  /*
   * An arrow with nothing to undo is greyed out, and a greyed-out button takes
   * no press at all. On a fresh page that is exactly the moment of the first
   * AI answer, so the arrow could not stop it (review round 9). While an
   * answer arrives both arrows are therefore live: their press stops it.
   */
  const [arriving, setArriving] = useState(anAnswerIsArriving());
  useEffect(() => {
    const look = () =>
      // After the box has drawn: the mark is read from the page.
      window.setTimeout(() => setArriving(anAnswerIsArriving()), 0);
    window.addEventListener(AI_BUSY_CHANGED, look);
    return () => window.removeEventListener(AI_BUSY_CHANGED, look);
  }, []);

  // A press that began on an arrow and was let go somewhere else must not
  // leave the mark behind for the next, unrelated press.
  useEffect(() => {
    const letGo = () =>
      window.setTimeout(() => {
        stoppedAnAnswer.current = false;
      }, 0);
    window.addEventListener('mouseup', letGo);
    return () => window.removeEventListener('mouseup', letGo);
  }, []);
  const press = (step: () => void) => () => {
    if (stoppedAnAnswer.current || anAnswerIsArriving()) {
      stoppedAnAnswer.current = false;
      stopTheArrivingAnswer();
      return;
    }
    step();
  };

  return (
    /*
     * Pressing an arrow must not take the caret out of the box the person is
     * typing in (found by Steven by hand, 3 Oct 2026: Ctrl/Cmd+Z kept the heading
     * active, the arrows did not). `data-keeps-editing` tells an open body-text
     * editor that this press is not "clicked somewhere else".
     *
     * Only for a field the page undo covers. The page title, the bullets and
     * the button text report their words when the caret LEAVES them, so there
     * the press must take the caret out first or the undo runs without knowing
     * what was just typed (review round 4, A3).
     */
    <div
      tw="flex items-center gap-1"
      data-keeps-editing
      onMouseDown={(event) => {
        // The left button, on an arrow itself: not the gap between them, not
        // a right-click.
        const onAnArrow =
          event.button === 0 &&
          !!(event.target as HTMLElement | null)?.closest?.('button');
        stoppedAnAnswer.current = onAnArrow && anAnswerIsArriving();
        if (stoppedAnAnswer.current) stopTheArrivingAnswer();
        if (caretIsInPageUndoField(document.activeElement))
          event.preventDefault();
      }}
    >
      <IconButton
        type="button"
        onClick={press(onUndo)}
        disabled={!canUndo && !arriving}
        aria-label="Undo"
        title="Undo"
        data-testid="undo-button"
      >
        <ArrowUUpLeft weight="bold" />
      </IconButton>
      <IconButton
        type="button"
        onClick={press(onRedo)}
        disabled={!canRedo && !arriving}
        aria-label="Redo"
        title="Redo"
        data-testid="redo-button"
      >
        <ArrowUUpRight weight="bold" />
      </IconButton>
    </div>
  );
};

const IconButton = styled.button`
  ${tw`p-2 rounded-[3px] text-gray-700 transition hover:bg-gray-100`}
  ${tw`disabled:(text-gray-300 cursor-not-allowed hover:bg-transparent)`}
`;

export default UndoRedo;
